import React, { useEffect, useCallback, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { useDispatch } from 'react-redux'
import { useSelector, useMakeSelector } from 'hooks/redux'

import strings from 'utils/strings'
import { PIN } from 'utils/constants'
import { isPinOutput, isCanOutput, isNativeOutput } from 'utils/types/features'
import { INTERNAL } from 'constants/featureTypes'

import { propEq, isNil, isEmpty, prop } from 'ramda'
import { mockEvent } from 'utils/events'

import outputSchema from 'schemas/validation/feature/outputSchema'
import { schemaProp } from 'schemas/validation'
import { updateOutputQuery } from 'actions/feature/outputs'
import {
  getOutputQuery,
  hasSingleCondition,
  getOutputPinSwitches,
} from 'selectors/feature/outputs'
import { makeConditionSelector } from 'selectors/feature/conditions'
import { OutputConnectorFinder } from 'components/feature/ConnectorFinderMenu'
import { TextInput } from 'components/patterns/Inputs'
import InputSwitchList from 'components/feature/NavigatorPanes/editors/base/InputSwitchList'
import OperatorEditor from 'components/feature/NavigatorPanes/editors/base/OperatorEditor'
import FixedOrFloatingEditor from 'components/feature/NavigatorPanes/editors/FixedOrFloatingEditor'
import Circuit from 'components/feature/Harnesses/Circuit'
import { getIsSchoolBusFeatureSubType } from 'selectors/feature/featureMetadata'
import OutputTypeEditor from '../OutputTypeEditor'
import OutputXmcPinEditor from '../OutputXmcPinEditor'
import OutputKeepAwakeSwitch from '../OutputKeepAwakeSwitch'
import OutputValueEditor from '../OutputValueEditor'
import OutputCanEditor from '../OutputCanEditor'
import {
  StyledOutputSelectMenu,
  StyledPowerNetLevelEditor,
} from './OutputFormSection.style'

const rule = schemaProp(outputSchema)

const getOutputLabel = (featureType, type) => {
  if (featureType === INTERNAL && isPinOutput(type)) return null
  if (isPinOutput(type)) return strings.output_type_pin

  return strings.output
}

/* eslint-disable react-hooks/exhaustive-deps */
const OutputFormSection = ({
  output,
  setPinDescription,
  featureModuleType,
  featureModuleName,
  updateNumberOfBits,
  updateStartBit,
  disabledBits,
  setSelectedOutput,
  handleSetOutputType,
  handleUpdateNumberOfBits,
  isDeletable,
  isTimerOutputQuery,
  updateOutputTimerValue,
  updateOutputOperator,
  allDescriptions,
  updatePowerNetLevel,
  updateOutputIsAwake,
  outputOptions,
  pinSet,
  featureType,
  setFloatingCircuit,
  validUris,
  isNativeOutput: hasNativeOutput,
}) => {
  const {
    mappedOutputId,
    type,
    polarity,
    numberOfBits,
    startBit,
    id,
    current,
    description,
    virtualFuseSetting,
    resetType,
    resetAttempts,
    operator,
    value = 0,
    powerNetLevel,
    isAwake,
    resetDelay,
    fnId,
    floatingCircuit,
    interfacePins,
  } = output || {}

  // This checks if we have a single condition query and uses that URI
  // or uses a null to break any associtions to previous output.
  const isSingleCondition = useSelector(hasSingleCondition, { fnId })
  const isSchoolBusFeature = useSelector(getIsSchoolBusFeatureSubType)
  const [filteredOutputOptions, setFilteredOutputOptions] = useState([])
  const outputQuery = useSelector(getOutputQuery, { fnId })
  const outputPinSwitches = useSelector(getOutputPinSwitches, {
    mappedOutputId,
  })
  const condition = useMakeSelector(makeConditionSelector, {
    queryUri: prop('uri', outputQuery),
  })
  const singleConditionUri = isSingleCondition ? prop('uri', condition) : null

  useEffect(() => {
    if (
      !isCanOutput(type) &&
      !outputOptions.find(propEq('value', mappedOutputId)) &&
      isNil(floatingCircuit)
    ) {
      updateOutput()
    }
    // TODO:
    // BUG 47463
    // The below dispatch should be removed.
    // When to move a logic set or timer back to the orphan col should be
    // handled in a saga that watches for changes to output.
    // In this case, sepcifically when 2-bit CAN is changed to 16-bit CAN.
    // A logic set cannot control 16-bit CAN and should moved back to orphan col.
    if ((isEmpty(validUris) && isCanOutput(type)) || singleConditionUri) {
      dispatch(updateOutputQuery(singleConditionUri, fnId))
    }
  }, [polarity, current, type, validUris, singleConditionUri])

  const dispatch = useDispatch()

  const updateOutput = useCallback(
    (updatedOutputId = null) => {
      if (updatedOutputId && !isNil(startBit)) {
        updateStartBit(undefined)
        updateNumberOfBits(mockEvent(undefined))
      }
      if (updatedOutputId !== null) {
        setSelectedOutput(updatedOutputId)
      }
      if (isNativeOutput(type)) {
        dispatch(updateOutputQuery(singleConditionUri, fnId))
      }
    },
    [startBit]
  )
  // disable can when feature is ASAM and there are greater than 1 outputs
  const pinSelected = useMemo(() => isPinOutput(type), [type])
  const showCircuit = type === PIN
  const outputLabel = useMemo(
    () => getOutputLabel(featureType, type),
    [featureType, type]
  )

  const handleNumberOfBitsChange = useCallback((event) => {
    handleUpdateNumberOfBits(event)
  }, [])

  useEffect(() => {
    if (isSchoolBusFeature && type !== 'PIN') {
      const updatedOutputOptions = outputOptions.filter(
        (item) => item.vehicleSubType === 'BUS'
      )
      setFilteredOutputOptions(updatedOutputOptions)
    } else {
      const updatedOutputOptions = outputOptions.filter(
        (item) => item.vehicleSubType !== 'BUS'
      )
      setFilteredOutputOptions(updatedOutputOptions)
    }
  }, [outputOptions, isSchoolBusFeature])

  const hasSwitches = !isEmpty(outputPinSwitches)

  const showConnectorFinder = mappedOutputId && type === PIN

  return (
    <>
      <TextInput
        rules={rule('description', { descriptions: allDescriptions })}
        name={strings.output_pin_name}
        label={strings.output_description}
        placeholder={strings.output_pin_name}
        value={description}
        onChange={setPinDescription}
      />

      <OutputTypeEditor
        output={output}
        setOutputType={handleSetOutputType}
        isDeletable={isDeletable}
      />

      {pinSelected && (
        <OutputXmcPinEditor
          outputId={id}
          polarity={polarity}
          current={current}
          virtualFuseSetting={virtualFuseSetting}
          resetType={resetType}
          resetAttempts={resetAttempts}
          resetDelay={resetDelay}
          fnId={fnId}
          featureModuleType={featureModuleType}
        />
      )}

      {isCanOutput(type) && (
        <OutputCanEditor
          featureModuleType={featureModuleType}
          mappedOutputId={mappedOutputId}
          numberOfBits={numberOfBits}
          startBit={startBit}
          updateStartBit={updateStartBit}
          updateNumberOfBits={handleNumberOfBitsChange}
          disabledBits={disabledBits}
          featureModuleName={featureModuleName}
        />
      )}
      <FixedOrFloatingEditor
        floatingValue={floatingCircuit}
        onFloatingChange={setFloatingCircuit}
        floatingActive={isPinOutput(type)}
        floatingRules={rule('floatingCircuit')}
        outputCurrent={current}
      >
        {type && !isCanOutput(type) && (
          <>
            <StyledOutputSelectMenu
              className="form-field"
              rules={rule('mappedOutputId', {
                pinSet,
                type,
                featureModuleName,
                featureType,
              })}
              style={{ width: '100%' }}
              value={mappedOutputId || undefined}
              label={outputLabel}
              name={strings.output}
              onChange={updateOutput}
              options={filteredOutputOptions}
              placeholder={strings.select}
              showSearch
              filterOption
              data-testid="pin-select"
            />
            {showConnectorFinder && (
              <OutputConnectorFinder
                interfacePins={interfacePins}
                output={output}
              />
            )}
            {showCircuit && <Circuit id={mappedOutputId} outputId={id} />}
            {hasSwitches && (
              <InputSwitchList
                currentInputOrOutputSwitches={outputPinSwitches}
                marginTop={'0px'}
              />
            )}
          </>
        )}
      </FixedOrFloatingEditor>

      {pinSelected && (
        <>
          <OutputKeepAwakeSwitch
            isAwake={isAwake}
            onChange={updateOutputIsAwake}
          />
          <StyledPowerNetLevelEditor
            powerNetLevel={powerNetLevel}
            handleChange={updatePowerNetLevel}
          />
        </>
      )}
      {isTimerOutputQuery && !hasNativeOutput && (
        <>
          <OperatorEditor
            operator={operator}
            onOperatorChange={updateOutputOperator}
          />

          <OutputValueEditor
            value={value}
            onOutputValueChange={updateOutputTimerValue}
          />
        </>
      )}
    </>
  )
}

OutputFormSection.propTypes = {
  featureModuleType: PropTypes.string.isRequired,
  featureModuleName: PropTypes.string.isRequired,
  output: PropTypes.shape({
    current: PropTypes.number,
    polarity: PropTypes.string,
    type: PropTypes.string,
    activeStateRange: PropTypes.array,
    description: PropTypes.string,
    location: PropTypes.string,
    connector: PropTypes.string,
    mappedOutputId: PropTypes.string,
    outputDescription: PropTypes.string,
    id: PropTypes.string,
    fnId: PropTypes.string,
    value: PropTypes.number,
    operator: PropTypes.number,
    resetDelay: PropTypes.number,
    switchNumber: PropTypes.string,
  }).isRequired,
  setPinDescription: PropTypes.func,
  setSelectedOutput: PropTypes.func,
  onValidityChange: PropTypes.func,
  updateNumberOfBits: PropTypes.func,
  updateStartBit: PropTypes.func,
  updateOutputOperator: PropTypes.func,
  updateOutputTimerValue: PropTypes.func,
  disabledBits: PropTypes.array,
  handleSetOutputType: PropTypes.func,
  handleUpdateNumberOfBits: PropTypes.func,
  updateOutputIsAwake: PropTypes.func,
  disableSixteenBit: PropTypes.bool,
  isDeletable: PropTypes.bool,
  isTimerOutputQuery: PropTypes.bool,
  allDescriptions: PropTypes.arrayOf(PropTypes.string),
  updatePowerNetLevel: PropTypes.func,
  outputOptions: PropTypes.array.isRequired,
  pinSet: PropTypes.object,
  featureType: PropTypes.string,
  switchDescription: PropTypes.string,
  setFloatingCircuit: PropTypes.func,
  /** which queries may be used as the output query/inputs list */
  validUris: PropTypes.array,
  isNativeOutput: PropTypes.bool,
}

export default OutputFormSection
